import { CoreApiClient } from '../../core/client'
import { withQuery } from '../../core/query'
import { requirePaymentFactRequest } from '../../payment-fact'
import type {
  CancelCommonOrderRequest,
  CommonOrder,
  CommonOrderDetail,
  CommonOrderQuery,
  MarkCommonOrderPaidRequest
} from '../mall'

export interface Product {
  id: number
  tenantId: number
  sku: string
  name: string
  category: string
  price: number
  stock: number
  ownerUserId?: number | null
  ownerDeptId?: number | null
  createdAt: string
}

export interface CreateProductRequest {
  sku: string
  name: string
  category: string
  price: number
  stock: number
  ownerUserId?: number
  ownerDeptId?: number
}

export interface UpdateProductRequest {
  name?: string
  category?: string
  price?: number
  stock?: number
}

export interface PurchaseOrder {
  id: number
  tenantId: number
  productId: number
  productName: string
  quantity: number
  unitCost: number
  totalCost: number
  status: 'DRAFT' | 'APPROVED'
  buyerUserId?: number | null
  buyerDeptId?: number | null
  createdAt: string
}

export interface CreatePurchaseOrderRequest {
  productId: number
  quantity: number
  unitCost: number
  buyerUserId?: number
  buyerDeptId?: number
}

export interface SubmitClientOrderRequest {
  productId: number
  quantity: number
  channel: string
  idempotencyKey: string
}

export interface SubmitClientOrderResponse {
  orderId: number
  commonOrderId?: number | null
  commonOrderNo?: string | null
  duplicated: boolean
  status: string
}

export interface TenantErpStatusView {
  tenantId: number
  productCount: number
  purchaseOrderCount: number
  pendingPurchaseOrderCount: number
  storageMode: string
}

export const ERP_PERMISSIONS = {
  adminInspect: 'platform:erp:inspect',
  workbenchView: 'erp:workbench:view',
  decorationView: 'erp:decoration:view',
  decorationManage: 'erp:decoration:manage',
  productView: 'erp:product:view',
  productCreate: 'erp:product:create',
  productUpdate: 'erp:product:update',
  purchaseView: 'erp:purchase:view',
  purchaseCreate: 'erp:purchase:create',
  purchaseApprove: 'erp:purchase:approve',
  clientProductView: 'erp:client:product:view',
  clientOrderSubmit: 'erp:client:order:submit'
} as const

export const ERP_PERMISSION_LIST = Object.values(ERP_PERMISSIONS)

export class ErpApiModule {
  private readonly client: CoreApiClient

  constructor(client: CoreApiClient) {
    this.client = client
  }

  getSaasErpCommonOrders(query: CommonOrderQuery = {}) {
    return this.client.get<CommonOrder[]>(withQuery('/api/saas/erp/common-orders', query))
  }

  getSaasErpCommonOrder(orderId: number) {
    return this.client.get<CommonOrderDetail>(`/api/saas/erp/common-orders/${orderId}`)
  }

  markSaasErpCommonOrderPaid(orderId: number, body: MarkCommonOrderPaidRequest) {
    return this.client.post<CommonOrder>(
      `/api/saas/erp/common-orders/${orderId}/paid`,
      requirePaymentFactRequest(body)
    )
  }

  markSaasErpCommonOrderFulfilling(orderId: number) {
    return this.client.post<CommonOrder>(`/api/saas/erp/common-orders/${orderId}/fulfilling`)
  }

  cancelSaasErpCommonOrder(orderId: number, body: CancelCommonOrderRequest = {}) {
    return this.client.post<CommonOrder>(`/api/saas/erp/common-orders/${orderId}/cancel`, body)
  }

  completeSaasErpCommonOrder(orderId: number) {
    return this.client.post<CommonOrder>(`/api/saas/erp/common-orders/${orderId}/complete`)
  }

}
